<template>
  <div
    class="loader"
    :class="[`loader--${props.variant}`, { 'loader--eager': props.loading === 'eager' }]"
  ></div>
</template>

<script setup lang="ts">
interface IProps {
  variant?: 'regular' | 'dark';
  loading?: 'eager' | 'lazy';
}

const props = withDefaults(defineProps<IProps>(), {
  variant: 'regular',
  loading: 'lazy',
});
</script>

<style scoped lang="postcss">
/* Initial loader styling */
.loader {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-height: 60px;

  &.loader--eager {
    z-index: -1;
  }

  &:before,
  &:after {
    display: block;
    content: '';
    position: absolute;
  }

  &:before {
    aspect-ratio: 1/1;
    top: 50%;
    left: 50%;
    width: auto;
    height: 40%;
    max-height: 200px;
    background-image: url('data:image/svg+xml;base64,PHN2ZyBmaWxsPSJub25lIiBoZWlnaHQ9IjE0NSIgdmlld0JveD0iMCAwIDIyNyAxNDUiIHdpZHRoPSIyMjciIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGcgZmlsbD0iI2ZmZiIgZmlsbC1vcGFjaXR5PSIuOCI+PHBhdGggZD0ibTY4LjA1NDYuMDI1NTEyN3YxNDQuNjAwNDg3M2gtMzQuODc2NHYtMTExLjk3OTloLS44NjE4bC0zMi4zMTY0IDE5Ljc3MDF2LTMwLjIxMjhsMzUuNjM2OC0yMi4xNzc4ODczeiIvPjxwYXRoIGQ9Im0xNzcuNzI4IDE0NC42MjZoLTM5LjU0bC00OC43OTEzLTE0NC42MDA1ODY1aDQxLjU0MjNjLS4yMi0uMjM0OTY1NSAxNS41MjIgNDguMzQzNzg2NSAyOC44MDMgODkuMjUwNTg2NS43MjQgMi4yMjg1IDE3Ljk4NiA1NS4zNSAxNy45ODYgNTUuMzV6Ii8+PHBhdGggZD0ibTE4Ny40ODcuMDAwMDYxMDRoMzkuMTA5bC00OC44NjggMTQ0LjYyNTkzODk2LTE2LjAzNi00OS4zNDQ0Yy0uMjg0LS44NzQyLTEuODEtNS41NzM2LTEuOTUtNi4wMDU2LjAxNS4wNDc1LjAzMy0uMTA1NSAwIDAgMTIuNjk0LTQwLjc1MDYgMjcuNzQ1LTg5LjI3NTkzODk2IDI3Ljc0NS04OS4yNzU5Mzg5NnoiLz48L2c+PC9zdmc+');
    background-position: center;
    background-repeat: no-repeat;
    background-size: 100%;
    transform: translate(-50%, -50%);
    z-index: 0;
  }

  &:after {
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      116.4deg,
      hsl(var(--color-primary-100), 0) 36.86%,
      hsl(var(--color-primary-100), 0.35) 45.37%,
      hsl(var(--color-primary-100), 0.5) 53.89%,
      hsl(var(--color-primary-100), 0.35) 62.4%,
      hsl(var(--color-primary-100), 0) 70.91%
    );
    animation: background-swoosh 2s 1s ease-in-out infinite;
    background-size: 300%;
    z-index: 0;
  }

  &.loader--regular {
    background: hsl(var(--color-primary-100));
  }

  &.loader--dark {
    background: hsl(var(--color-primary-400));

    &:before {
      opacity: 0.75;
    }
  }
}

@keyframes background-swoosh {
  0% {
    background-position: 150% 50%;
  }

  100% {
    background-position: 0 50%;
  }
}
</style>
